import React from "react";
import { Formik, Form as FormikForm } from "formik";
import { Button, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaUser,
  FaBuilding,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaPaperPlane,
  FaBoxOpen,
  FaTruck,
  FaClipboardList,
  FaCheckCircle,
} from "react-icons/fa";
import { quotesApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import FormField from "../components/forms/FormField";
import { quoteSchema } from "../validation/schemas";
import "./Quote.css";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../constants/seo";

const Quote = () => {
  const { user } = useAuth();

  const initialValues = {
    name: user?.name || "",
    company: "",
    phone: user?.phone || "",
    email: user?.email || "",
    category: "",
    quantity: "",
    location: "",
    message: "",
  };

  return (
    <main className="quote-page">
      <Seo {...PAGE_SEO.quote} />
      <section className="quote-hero">
        <span className="quote-badge">Request a Quote</span>
        <h1>Get a Custom Quote for Your Requirements</h1>
        <p>
          Share your office, school, stationery, or signage requirements with us.
          Our team will review your request and get back to you quickly.
        </p>
      </section>

      <section className="quote-section">
        <div className="quote-info">
          <span className="quote-badge">Why Choose Us</span>
          <h2>Fast quotations for bulk orders and business needs.</h2>
          <p>
            Whether you need office essentials, school supplies, stationery
            products, or Sign & Graphics solutions, SG Office helps you receive
            quick and reliable quotation support.
          </p>

          <div className="quote-points">
            <div className="quote-point">
              <FaBoxOpen />
              <div>
                <h4>Bulk Orders</h4>
                <p>Ideal for offices, schools, and businesses.</p>
              </div>
            </div>
            <div className="quote-point">
              <FaClipboardList />
              <div>
                <h4>Custom Requirements</h4>
                <p>Send product lists, quantities, and special needs.</p>
              </div>
            </div>
            <div className="quote-point">
              <FaTruck />
              <div>
                <h4>UAE Delivery Support</h4>
                <p>Quick delivery support across UAE locations.</p>
              </div>
            </div>
            <div className="quote-point">
              <FaCheckCircle />
              <div>
                <h4>Trusted Since 2010</h4>
                <p>Reliable office supply partner for businesses.</p>
              </div>
            </div>
          </div>

          <a
            href="https://wa.me/971509348007?text=Hi%2C%20I%20would%20like%20to%20request%20a%20quote."
            target="_blank"
            rel="noreferrer"
            className="quote-whatsapp-btn"
          >
            <FaWhatsapp />
            Request Quote on WhatsApp
          </a>
        </div>

        <div className="quote-form-wrapper">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={quoteSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                const res = await quotesApi.create(values);
                const ref = res.data.data.quote?.quoteNumber;
                toast.success(
                  ref
                    ? `Thank you! Your quote reference is ${ref}.`
                    : "Thank you! Your quote request has been submitted."
                );
                resetForm({
                  values: {
                    ...initialValues,
                    company: "",
                    category: "",
                    quantity: "",
                    location: "",
                    message: "",
                  },
                });
              } catch (error) {
                toast.error(
                  getErrorMessage(error, "Could not submit quote request")
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <FormikForm className="quote-form" noValidate>
                <h3>Quote Request Form</h3>

                <Row>
                  <Col md={6}>
                    <FormField
                      name="name"
                      label="Full Name"
                      labelIcon={<FaUser />}
                      placeholder="Enter your name"
                    />
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="company"
                      label="Company Name"
                      labelIcon={<FaBuilding />}
                      placeholder="Company name"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormField
                      name="phone"
                      type="tel"
                      label="Phone"
                      labelIcon={<FaPhoneAlt />}
                      placeholder="Enter phone number"
                    />
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="email"
                      type="email"
                      label="Email"
                      labelIcon={<FaEnvelope />}
                      placeholder="Enter email address"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormField
                      name="category"
                      label="Product / Category"
                      labelIcon={<FaBoxOpen />}
                    >
                      <option value="">Select category</option>
                      <option value="Files & Folders">Files & Folders</option>
                      <option value="Writing Instruments">
                        Writing Instruments
                      </option>
                      <option value="Office Equipment">Office Equipment</option>
                      <option value="Office Machinery">Office Machinery</option>
                      <option value="Educational Products">
                        Educational Products
                      </option>
                      <option value="Arts & Crafts">Arts & Crafts</option>
                      <option value="Sports & Leisure">Sports & Leisure</option>
                      <option value="Sign & Graphics">Sign & Graphics</option>
                      <option value="Other">Other</option>
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="quantity"
                      label="Quantity"
                      labelIcon={<FaClipboardList />}
                      placeholder="Example: 50 pcs / bulk order"
                    />
                  </Col>
                </Row>

                <FormField
                  name="location"
                  label="Delivery Location"
                  labelIcon={<FaMapMarkerAlt />}
                  placeholder="Example: Al Quoz, Dubai"
                />

                <FormField
                  name="message"
                  type="textarea"
                  rows={5}
                  label="Requirement Details"
                  placeholder="Describe your requirement, product list, size, quantity, or special instructions..."
                />

                <Button
                  type="submit"
                  className="quote-submit-btn"
                  disabled={isSubmitting}
                >
                  <FaPaperPlane />{" "}
                  {isSubmitting ? "Submitting..." : "Submit Quote Request"}
                </Button>
              </FormikForm>
            )}
          </Formik>
        </div>
      </section>
    </main>
  );
};

export default Quote;
